import React, { useState, useEffect, useContext } from 'react'
import axios from '../../../config/axios'
import { GlobalTable, HeaderLine, Td, Th, Tr } from '../../../components/styles/table-style'
import OrdersContext from '../../../contexts/OrdersContext'
import ModalContext from '../../../contexts/ModalContext'
import LoadMoreButton from '../../../components/LoadMoreButton'

// Interface para os dados dos pedidos do Ploomes
interface PloomesOrder {
  id: number
  title: string
  amount?: number
  stage?: {
    id: number
    name: string
  }
  person?: {
    name: string
  }
  win_time?: string
  other_properties?: any
  originDealId?: number
  statusId?: number
}

// Componente para mostrar pedidos do Ploomes
const PloomesOrdersTable: React.FC = () => {
  const [orders, setOrders] = useState<PloomesOrder[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Contextos para o modal de detalhes
  const { setOrderId, setOriginDealId, setStatus, setStatusId } = useContext(OrdersContext)
  const { setModalActive, setModalSection } = useContext(ModalContext)

  useEffect(() => {
    const fetchPloomesOrders = async () => {
      try {
        setLoading(true)
        // Usando o endpoint correto baseado no componente existente
        const response = await axios.post(`${process.env.REACT_APP_ORDERS_BASE_URL}/orders-by-ploomes/list`)
        setOrders(response.data || [])
      } catch (err: any) {
        console.error('Erro ao buscar pedidos do Ploomes:', err)
        setError('Erro ao carregar pedidos do Ploomes')
      } finally {
        setLoading(false)
      }
    }

    fetchPloomesOrders()
  }, [])

  const handleOrderClick = (order: PloomesOrder) => {
    // Configurar o contexto do pedido e abrir o modal de detalhes
    setOrderId(order.id)
    setOriginDealId(order.originDealId || order.id)
    setStatus(order.stage?.name || '')
    setStatusId(order.stage?.id || order.statusId || 0)
    
    // Ativar o modal
    setModalActive(true)
    setModalSection('ordersDetails')
  }

  const columns = [
    "ID",
    "Título", 
    "Cliente",
    "Valor",
    "Status",
    "Data",
    "Ações"
  ]

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Carregando pedidos...
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        {error}
      </div>
    )
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px' }}>Rastreio de Pedidos - Ploomes</h2>
      <GlobalTable>
        <thead>
          <HeaderLine>
            {columns.map((column, index) => (
              <Th key={index}>{column}</Th>
            ))}
          </HeaderLine>
        </thead>
        <tbody>
          {orders.slice(0, visibleCount).map((order) => (
            <Tr key={order.id}>
              <Td>{order.id}</Td>
              <Td>{order.title || 'N/A'}</Td>
              <Td>{order.person?.name || 'N/A'}</Td>
              <Td>
                {order.amount 
                  ? `R$ ${order.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` 
                  : 'N/A'
                }
              </Td>
              <Td>{order.stage?.name || 'N/A'}</Td>
              <Td>
                {order.win_time 
                  ? new Date(order.win_time).toLocaleDateString('pt-BR') 
                  : 'N/A'
                }
              </Td>
              <Td>
                <button 
                  onClick={() => handleOrderClick(order)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Ver Detalhes
                </button>
              </Td>
            </Tr>
          ))}
        </tbody>
      </GlobalTable>
      {orders.length === 0 && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Nenhum pedido encontrado
        </div>
      )}
      <LoadMoreButton
        currentCount={Math.min(visibleCount, orders.length)}
        totalCount={orders.length}
        hasMore={visibleCount < orders.length}
        onLoadMore={() => setVisibleCount(prev => prev + 50)}
        label="pedidos"
      />
    </div>
  )
}

/**
 * General Orders component that handles order tracking
 * - Shows Ploomes Orders tracking (external API)
 * - This provides a unified entry point for "Rastreio de pedidos"
 */
const Orders: React.FC = () => {
  // Para todos os tenants, mostrar o rastreio de pedidos do Ploomes
  return <PloomesOrdersTable />
}

export default Orders